// src/cutout.ts
var cutoutStyles = ["bump", "sawtooth", "stamp", "wave", "zigzag"];
var cutoutEdge = [
  "y",
  "top bottom",
  "top",
  "bottom",
  "x",
  "left right",
  "left",
  "right"
];
var styleRegexp = `(?:${cutoutStyles.join("|")})`;
var edgeRegexp = `(?:${cutoutEdge.join("|")})(?:@(?:small|large))?`;
function isValidCutoutStyle(value) {
  return new RegExp(styleRegexp, "i").test(value);
}
function isValidCutoutEdge(value) {
  return new RegExp(`^${edgeRegexp}$`, "i").test(value);
}
function isValidCutoutAttribute(value) {
  return new RegExp(`^${styleRegexp} ${edgeRegexp}$`, "i").test(value);
}

// src/ensure-instance-of-error.ts
var UnknownErrorType = class extends Error {
  constructor(unknownError) {
    super("unknown error type");
    this.unknownError = unknownError;
  }
};
var ensureInstanceOfError = (err) => {
  if (err instanceof Error) {
    return err;
  }
  if (typeof err === "string") {
    return new Error(err);
  }
  return new UnknownErrorType(err);
};

// src/feature-flags.ts
var featureFlagParamName = "feat-flag";
function featureFlagsFromQuery(query) {
  const flags = [];
  query.forEach((value, key) => {
    const match = key.match(
      /^feat-flag\[(\d+)\]\[(featureKey|ruleKey|variationKey)\]$/
    );
    if (match) {
      const index = parseInt(match[1], 10);
      const field = match[2];
      if (!flags[index]) {
        flags[index] = { featureKey: "", ruleKey: null, variationKey: null };
      }
      flags[index][field] = value;
    }
  });
  return flags;
}
function parseFeatureFlags(flags) {
  return new URLSearchParams(
    Object.entries(flags).reduce((result, [_, flag], index) => {
      result.push([
        `${featureFlagParamName}[${index}][featureKey]`,
        flag.featureKey
      ]);
      result.push([
        `${featureFlagParamName}[${index}][ruleKey]`,
        flag.ruleKey || ""
      ]);
      result.push([
        `${featureFlagParamName}[${index}][variationKey]`,
        flag.variationKey || ""
      ]);
      return result;
    }, [])
  );
}

// src/initial-event.ts
var formatInitialEvent = (app, marketplace, pageType, orderNumber) => {
  return {
    event: "init",
    app,
    content_locale: marketplace.contentLocales[0],
    country: marketplace.iso,
    currency: marketplace.currency,
    // Prefix is required to generate the correct URL for the datalayer
    // at least, until the data-layer endpoint is moved to frontend.
    prefix: marketplace.iso === "US" ? "" : marketplace.identifier,
    marketplace: marketplace.identifier,
    type: pageType,
    ...orderNumber && { order_number: orderNumber }
  };
};

// src/is-browser.ts
function isBrowser() {
  return typeof window !== "undefined";
}

// src/string-manipulation.ts
function trimCharacter(path, char) {
  if (char.length > 1)
    throw new Error(
      "trimCharacter can only trim a single character, as the name implied."
    );
  return path.slice(
    path[0] === char ? 1 : 0,
    path[path.length - 1] === char ? -1 : void 0
  );
}

// src/locale.ts
var DEFAULT_LOCALE = "us";
var localeRegexp = /^[a-z]{2}(?:-[a-z]{2})?$/i;
function isLocalisedPath(path) {
  const parts = trimCharacter(path, "/").split("/");
  if (!parts.length || !isValidLocale(parts[0]))
    return false;
  return true;
}
function getUnlocalisedPathname(path) {
  return path.replace(/^\/?[a-z]{2}(?:-[a-z]{2})?(\/|$)/i, "$1");
}
function isValidLocale(locale) {
  return localeRegexp.test(locale);
}

// src/mq.ts
function createMinWidthQuery(width) {
  return `(min-width: ${width}px)`;
}
function createMaxWidthQuery(width) {
  return `(max-width: ${width}px)`;
}
function createMinHeightQuery(height) {
  return `(min-height: ${height}px)`;
}
function createMaxHeightQuery(height) {
  return `(max-height: ${height}px)`;
}
function createReducedMotionQuery(reduce = true) {
  return `(prefers-reduced-motion: ${reduce ? "reduce" : "no-preference"})`;
}

// src/palette.ts
var paletteColours = [
  "bright-smile",
  "kraft-paper",
  "sharpie",
  "tinkerbell",
  "lily-pad",
  "racing-green",
  "butter",
  "mustard",
  "ketchup",
  "bubble-bath",
  "paper-boat",
  "blueberry",
  "princess",
  "pinking-shears",
  "cherry-cola"
];
function isValidPaletteColor(value) {
  return paletteColours.includes(value.toLocaleLowerCase());
}
function isValidPaletteAttribute(value) {
  return new RegExp(`^(?:${paletteColours.join("|")})(?: accent)?$`, "i").test(
    value
  );
}

// src/parse-cookies.ts
function parseCookies(cookies) {
  return cookies.split(/;\s?/).filter((s) => s).reduce((obj, cookie) => {
    const matches = cookie.match(/(.+?)=(.*)/);
    if (matches === null)
      return obj;
    const [, key, value] = matches;
    obj[key] = decodeURIComponent(value);
    return obj;
  }, {});
}

// src/query-string.ts
function parse(query) {
  return new URLSearchParams(
    Object.entries(query).reduce((result, [key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => result.push([key, v.toString()]));
      } else if (typeof value === "boolean" || typeof value === "number") {
        result.push([key, value.toString()]);
      } else if (typeof value === "string") {
        result.push([key, value]);
      }
      return result;
    }, [])
  );
}
function stringify(query, includePrefix = false) {
  const string = parse(query).toString();
  if (includePrefix && string)
    return "?" + string;
  return string;
}

// src/replace-text.ts
function replaceText(text, replacements = {}) {
  return Object.keys(replacements).reduce(
    (str, key) => str.replaceAll(`%{${key}}`, replacements[key].toString()),
    text
  );
}

// src/routes.ts
var param = "[-A-Za-z\xC0-\xD6\xD8-\xF6\xF8-\xFF0-9]+";
function routeTest(route, slug) {
  return typeof route === "string" ? route.toLowerCase() === slug.toLowerCase() : route.test(slug);
}
function isValidRoute(routes, slug) {
  return routes.some((route) => routeTest(route, slug));
}
function matchRoute(routes, slug) {
  return routes.find((route) => routeTest(route, slug));
}
function getDomain(hostname) {
  if (!hostname)
    return void 0;
  const parts = hostname.split(".");
  if (parts.length === 2)
    return hostname;
  return parts.slice(1).join(".").replace(/:[0-9]{4}/, "");
}

// src/url.ts
var pattern = /([-a-z0-9]+)(?:_([-a-z0-9]+))?_([-a-z]+)/i;
var default_marketplace = "us";
function getUrlPrefix(locale) {
  return locale === default_marketplace ? "" : locale;
}
function getLocaleUrl(locale, path) {
  if (/^(https?)?:\/\//.test(path))
    return path;
  const prefix = getUrlPrefix(locale);
  if (!path?.startsWith("/"))
    path = "/" + path;
  if (!prefix)
    return path;
  return `/${prefix}${path}`;
}
function removeLocalePrefix(locale, path) {
  const regex = new RegExp(`^/${locale}(/|$)`);
  return path.replace(regex, "/");
}
function getCanonicalPath(marketplaceId, pathname) {
  if (marketplaceId === default_marketplace) {
    const cleanedPath = pathname.replace(/^\/us(\/|$)/, "/");
    return cleanedPath === "/" ? "" : cleanedPath.replace(/\/$/, "");
  }
  return pathname.replace(/\/$/, "");
}
function parseUid(uid) {
  const hasValidId = uid?.match(pattern);
  if (!hasValidId) {
    throw new Error("Invalid UID pattern or missing UID");
  }
  const parts = uid.split("_");
  const locale = parts.pop();
  const pageType = parts.shift();
  const slug = parts.join("_");
  return {
    pageType: pageType || "",
    locale: locale || default_marketplace,
    slug
  };
}
function getUrlFromUid(uid, marketplace) {
  const { pageType, slug } = parseUid(uid);
  const params = {};
  const route = getRouteForPageType(pageType, slug, params);
  const qs = stringify(params, true);
  const url = `/${route}${qs}`;
  if (marketplace === default_marketplace) {
    return url;
  }
  return `/${marketplace}${url}`;
}
function getRouteForPageType(pageType, slug, params) {
  switch (pageType) {
    case "shipping-details-variant":
    case "shipping-details":
      if (slug)
        params.contentVariant = slug;
      return `checkout-v2/address`;
    case "shipping-methods-variant":
    case "shipping-methods":
      if (slug)
        params.contentVariant = slug;
      return `checkout-v2/delivery`;
    case "payment-methods-variant":
    case "payment-methods":
      if (slug)
        params.contentVariant = slug;
      return `checkout-v2/payment`;
    case "product-variant":
      return `landing-pages/personalized-products/product/${slug}`;
    case "range-variant":
      return `landing-pages/personalized-products/${slug}`;
    case "homepage-variant":
      if (slug)
        params.contentVariant = slug;
      return "";
    case "options":
      return `personalized-products/${slug}/options`;
    case "canvas":
      return `personalized-products/${slug}/preview`;
    case "product":
      return `personalized-products/${slug}`;
    case "product-download":
      return `personalized-downloads/${slug}`;
    case "product-range":
      return `personalized-products`;
    case "choose-your-country":
      return `choose-your-country`;
    case "login":
      return "login";
    case "search-links":
      return "search-links";
    case "signup-existing":
      return "signup/existing";
    case "signup":
      return "signup";
    case "account-profile":
      if (slug)
        params.contentVariant = slug;
      return "account/profile";
    case "account-offers":
      if (slug)
        params.contentVariant = slug;
      return "account/offers";
    case "account-credit":
      if (slug)
        params.contentVariant = slug;
      return "account/credit";
    case "account-dashboard":
      if (slug)
        params.contentVariant = slug;
      return "account";
    case "orders":
      if (slug)
        params.contentVariant = slug;
      return "account/orders";
    case "order-details":
      if (slug)
        params.contentVariant = slug;
      return "account/orders/%{orderNumber}";
    case "pages":
      return `pages/${slug}`;
    case "offers-variant":
      return `offers/${slug}`;
    case "offers":
      return "offers";
    case "cart":
    case "cart-variant":
      if (slug)
        params.contentVariant = slug;
      return `cart`;
    case "forgot-password":
      return `forgot-password`;
    case "confirm":
      return `confirm`;
    case "sitemap":
      return "sitemap";
    case "product-page":
      return `personalized-book/${slug}`;
    case "product-listing":
      if (slug) {
        const [fullSlug, variant] = slug.split("-variant-");
        const baseSlug = fullSlug.replace(new RegExp("_", "g"), "/");
        const prefix = baseSlug !== "personalized-books" ? "personalized-books/" : "";
        return variant ? `${prefix}${baseSlug}?contentVariant=${variant}` : `${prefix}${baseSlug}`;
      }
      break;
    case "continue-shopping":
      if (slug)
        return `continue-shopping/${slug}`;
      return "continue-shopping";
    case "continue-shopping-variant":
      if (slug)
        params.contentVariant = slug;
      return "continue-shopping";
    default:
      if (slug) {
        return `personalized-products/${slug}`;
      }
      return "";
  }
}

// src/date-formatter.ts
var detailedDateOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric"
};
var simpleDateOptions = {
  year: "numeric",
  month: "long",
  day: "numeric"
};
var formatDate = (input, locale, options = detailedDateOptions) => {
  const dateObject = new Date(input);
  try {
    return new Intl.DateTimeFormat(locale, options).format(dateObject);
  } catch (error) {
    return dateObject.toLocaleDateString(locale, options);
  }
};

// src/time-between.ts
var isValidDate = (date) => {
  return date instanceof Date;
};
var timeBetween = (from, to) => {
  let fromDate = to === void 0 ? /* @__PURE__ */ new Date() : from;
  let toDate = to || from;
  let seconds = 0;
  let minutes = 0;
  let hours = 0;
  let days = 0;
  if (!isValidDate(fromDate)) {
    fromDate = new Date(fromDate);
  }
  if (!isValidDate(toDate)) {
    toDate = new Date(toDate);
  }
  const hasDatePassed = toDate <= fromDate;
  if (!isValidDate(fromDate) || !isValidDate(toDate) || hasDatePassed) {
    return {
      seconds,
      minutes,
      hours,
      days
    };
  }
  seconds = Math.abs(toDate.getTime() - fromDate.getTime()) / 1e3;
  minutes = Math.floor(seconds / 60);
  hours = Math.floor(minutes / 60);
  days = Math.floor(hours / 24);
  return {
    seconds,
    minutes,
    hours,
    days
  };
};

// src/track-analytics.ts
function pushEvent(dataLayer, eventCategory, eventAction, eventLabel, eventValue) {
  const { type } = dataLayer?.find(
    (obj) => Object.hasOwn(obj, "app")
  ) || { type: "unknown page type" };
  const category = eventCategory || `${type}`;
  return dataLayer?.push({
    event: "track",
    // We want to use the existing GTM tag which is also used in v2 for Avatars, etc
    eventData: {
      category,
      action: eventAction,
      label: eventLabel,
      value: eventValue
    }
  });
}

// src/scrape-tracking-parameters.ts
var trackingParametersPattern = /_gl_[0-9a-zA-Z\D]*|_gcl_[0-9a-zA-Z\D]*|_ga|gclid|fbclid|e_token|localised_domain|sscid|om_[a-z]+|aa_query|aa_sku|aa_index|utm_|sc_|gbraid|wbraid|srsltid|ttclid/;
var scrapeTrackingParams = (searchParams) => {
  return Array.from(searchParams.entries()).reduce(
    (trackingParams, [key, value]) => {
      if (key.match(trackingParametersPattern)) {
        trackingParams[key] = value;
      }
      return trackingParams;
    },
    {}
  );
};

// src/get-currency-symbol.ts
var symbols = {
  USD: "$",
  EUR: "\u20AC",
  AUD: "$",
  CAD: "$",
  DKK: "kr",
  HKD: "$",
  INR: "\u20B9",
  NZD: "$",
  SGD: "$",
  SEK: "kr",
  GBP: "\xA3",
  KRW: "\u20A9",
  JPY: "\xA5"
};
function getCurrencySymbol(currency) {
  return symbols[currency];
}
export {
  DEFAULT_LOCALE,
  createMaxHeightQuery,
  createMaxWidthQuery,
  createMinHeightQuery,
  createMinWidthQuery,
  createReducedMotionQuery,
  cutoutEdge,
  cutoutStyles,
  detailedDateOptions,
  ensureInstanceOfError,
  featureFlagParamName,
  featureFlagsFromQuery,
  formatDate,
  formatInitialEvent,
  getCanonicalPath,
  getCurrencySymbol,
  getDomain,
  getLocaleUrl,
  getUnlocalisedPathname,
  getUrlFromUid,
  getUrlPrefix,
  isBrowser,
  isLocalisedPath,
  isValidCutoutAttribute,
  isValidCutoutEdge,
  isValidCutoutStyle,
  isValidLocale,
  isValidPaletteAttribute,
  isValidPaletteColor,
  isValidRoute,
  matchRoute,
  paletteColours,
  param,
  parse,
  parseCookies,
  parseFeatureFlags,
  parseUid,
  pushEvent,
  removeLocalePrefix,
  replaceText,
  scrapeTrackingParams,
  simpleDateOptions,
  stringify,
  timeBetween,
  trimCharacter
};
